import { Box, NeoColor, P4, Skeleton, Stack } from "@noom/wax-component-library";
import type { ReactNode } from "react";

import { AdInteractionType } from "gql/graphql";

import type {
  MarketplaceAd,
  MarketplacePlacement,
  UseMarketplaceAdsResult,
} from "../hooks/useMarketplaceAds";
import { useMarketplaceAds } from "../hooks/useMarketplaceAds";
import { openDeeplink } from "../utils";

import { HtmlContent } from "./HtmlContent";
import { MarketplaceCarousel } from "./MarketplaceCarousel";
import { MarketplaceErrorCard } from "./MarketplaceErrorCard";
import { useTrackAdInteractions } from "./hooks/useTrackAdInteractions";
import { useTrackMarketplaceViewed } from "./hooks/useTrackMarketplaceViewed";
import { buildMarketplaceDeeplink } from "./utils";

export type MarketplaceSectionHandlers = {
  onAdTapped: (ad: MarketplaceAd, placementName: string) => void;
  onAdViewed: (ad: MarketplaceAd, placementName: string) => void;
};

// Self-contained full-width white sheet. The Care tab shell no longer
// imposes a horizontal gutter, so the sheet spans its full column width on
// its own; `px={4}` insets the content (section titles, cards, disclaimers)
// 16px from the sheet edges. The carousel opts out of the right inset via a
// negative margin (see MarketplaceCarousel) so cards can bleed off-screen.
// `flexGrow` lets the sheet stretch to the bottom of the (min-height 100vh)
// page column so the white background never leaves a gray gap beneath it.
const MarketplaceContainer = ({ children }: { children: ReactNode }) => (
  <Box
    width="100%"
    px={4}
    pt={8}
    pb={4}
    bg={NeoColor.greyscale.white}
    borderTopRadius="2xl"
    flexGrow={1}
  >
    {children}
  </Box>
);

const MarketplaceSkeleton = () => (
  <Stack spacing={4} width="100%" data-cy="marketplace-loading">
    <Skeleton
      height={6}
      width="240px"
      borderRadius="md"
      startColor={NeoColor.greyscale.gray1}
      endColor={NeoColor.greyscale.gray1}
    />
    <Skeleton
      width="clamp(240px, calc(100vw - 63px), 312px)"
      borderRadius="xl"
      startColor={NeoColor.greyscale.gray1}
      endColor={NeoColor.greyscale.gray1}
      sx={{ aspectRatio: "312 / 292" }}
    />
  </Stack>
);

/**
 * Leaf component that renders the carousels AND fires the marketplace
 * page-view event — mounting it only when placements are ready keeps
 * the analytics aligned with what the user actually sees (same pattern
 * as `CareTabTreatmentList`).
 */
const MarketplaceCarouselList = ({
  placements,
  disclaimers,
  onAdTapped,
  onAdViewed,
}: {
  placements: MarketplacePlacement[];
  disclaimers: string[];
  onAdTapped: (ad: MarketplaceAd, placementName: string) => void;
  onAdViewed: (ad: MarketplaceAd, placementName: string) => void;
}) => {
  useTrackMarketplaceViewed();

  return (
    <Stack spacing={6} width="100%">
      <Stack spacing={10} width="100%">
        {placements.map((placement) => (
          <MarketplaceCarousel
            key={placement.placementName}
            placement={placement}
            onAdTapped={onAdTapped}
            onAdViewed={onAdViewed}
        />
        ))}
      </Stack>
      {disclaimers.length > 0 && (
        <Stack spacing={1}>
          {disclaimers.map((disclaimer) => (
            <P4 key={disclaimer} color="gray.500">
              <HtmlContent html={disclaimer} />
            </P4>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

/**
 * The Care tab marketplace: one carousel per ads placement returned by
 * the `placementGroupAds` query.
 *
 * States:
 *   - loading  → skeleton
 *   - error    → "Something went wrong" card with retry (all carousels hidden)
 *   - no ads   → the entire section disappears
 *   - success  → carousels
 */
export const MarketplaceSectionView = ({
  placements,
  disclaimers,
  loading,
  error,
  refetch,
  onAdTapped,
  onAdViewed,
}: UseMarketplaceAdsResult & MarketplaceSectionHandlers) => {
  if (loading) {
    return (
      <MarketplaceContainer>
        <MarketplaceSkeleton />
      </MarketplaceContainer>
    );
  }

  if (error) {
    return (
      <MarketplaceContainer>
        <Box data-cy="marketplace-error">
          <MarketplaceErrorCard onRetry={refetch} />
        </Box>
      </MarketplaceContainer>
    );
  }

  if (placements.length === 0) {
    return null;
  }

  return (
    <MarketplaceContainer>
      <div data-cy="marketplace-section">
        <MarketplaceCarouselList
          placements={placements}
          disclaimers={disclaimers}
          onAdTapped={onAdTapped}
          onAdViewed={onAdViewed}
        />
      </div>
    </MarketplaceContainer>
  );
};

export const useMarketplaceSectionHandlers = (): MarketplaceSectionHandlers => {
  const trackAdInteraction = useTrackAdInteractions();

  return {
    onAdTapped: (ad, placementName) => {
      trackAdInteraction(ad, placementName, AdInteractionType.Clicked);
      openDeeplink(buildMarketplaceDeeplink(ad.targetUrl));
    },
    onAdViewed: (ad, placementName) => {
      trackAdInteraction(ad, placementName, AdInteractionType.Viewed);
    },
  };
};

export const MarketplaceSection = () => {
  const marketplaceResult = useMarketplaceAds();
  const handlers = useMarketplaceSectionHandlers();

  return <MarketplaceSectionView {...marketplaceResult} {...handlers} />;
};
